import React, { Suspense, useEffect, useState } from "react";
import { Routes, useLocation } from "react-router-dom";
import { Toaster, sileo } from "sileo";
import { LayoutProvider, useLayout } from "@app/providers/LayoutContext";
import { DemoProvider } from "@app/providers/DemoContext";
import Layout from "./components/Layout";
import ThemeWrapper from "@app/providers/ThemeWrapper";
import "@app/styles/App.css";
import NotificacionPendientes from "@shared/ui/notificacion-pendientes/NotificacionPendientes";
import usePendientesNotificacion from "./services/PendientesNotificacionService";
import CumpleanosNotificacion from "@shared/ui/cumpleanos-notificacion/CumpleanosNotificacion";
import useCumpleanosNotificacion from "./services/CumpleanosNotificacionService";
import InAppNotifications from "./components/notifications/InAppNotification";
import InstallPWA from "../src/components/InstallPWA";
import UpdateNotification from "./components/notifications/UpdateNotification";
import { GlobalSkeletonProvider } from "@shared/ui/darpha-skeleton/DarphaSkeleton";
import DemoControls from "./components/DemoControls/DemoControls";
import allRoutes from "@app/routes"; // ✅ Importar todas las rutas desde /app/routes
import ErrorBoundary from "./components/ErrorBoundary"; // ✅ Sistema de captura de errores
import frontendErrorLogger from "./services/frontendErrorLogger"; // ✅ Logger de errores
import CookieConsent from "@features/legal/ui/components/CookieConsent"; // ✅ Banner de cookies
import {
  AUTH_STATE_CHANGED_EVENT,
  isAuthenticated as checkAuth,
} from "./utils/authStorage";
import apiClient from "@shared/config/axiosConfig";
import SessionExpiredModal from "./components/SessionExpiredModal/SessionExpiredModal"; // ✅ Modal de sesión expirada
import {
  connectSocket,
  disconnectSocket,
  getSocket,
} from "@shared/config/socketClient";
import { emitSessionExpired } from "./components/SessionExpiredModal/SessionExpiredModal";

// Componente que conecta Sileo con el tema de la app
function ThemedToaster() {
  const { isDarkTheme } = useLayout();
  return (
    <Toaster position="top-right" theme={isDarkTheme ? "light" : "dark"} />
  );
}

function App() {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Estado local para cerrar la notificación de cumpleaños manualmente
  const [cumpleDismissed, setCumpleDismissed] = useState(false);

  // ✅ Inicializar el sistema de captura de errores frontend
  useEffect(() => {
    frontendErrorLogger.initialize();
    console.log("✅ Sistema de captura de errores frontend activado");

    // Limpiar al desmontar
    return () => {
      frontendErrorLogger.disable();
    };
  }, []);

  // Reemplazar el useEffect de registro con este más seguro
  useEffect(() => {
    // En desarrollo usamos una ruta diferente que en producción
    const swPath = import.meta.env.DEV
      ? "/src/sw-custom.js" // Ruta para desarrollo
      : "/sw-custom.js"; // Ruta para producción

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistration().then((registration) => {
        if (!registration) {
          // Solo registrar si no hay registro previo
          navigator.serviceWorker
            .register(swPath)
            .then((reg) =>
              console.log("Service Worker registrado correctamente:", reg),
            )
            .catch((error) =>
              console.error("Error al registrar Service Worker:", error),
            );
        } else {
          console.log("Service Worker ya registrado:", registration);
        }
      });
    }
  }, []);

  // Notificaciones de pendientes
  const {
    pendientesSinCompletar,
    mostrarNotificacion,
    verificarPendientes,
    cerrarNotificacion,
  } = usePendientesNotificacion();

  // Notificaciones de cumpleaños
  const {
    cumpleaneros,
    esCumpleanos,
    mostrarNotificacion: mostrarNotificacionOriginal,
    nombreUsuario,
    verificarCumpleanos,
    cerrarNotificacion: cerrarNotificacionCumpleOriginal,
  } = useCumpleanosNotificacion();

  // Añadir en un useEffect en App.jsx
  useEffect(() => {
    // Pre-inicializar el Service Worker y la conexión push
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        console.log("Service Worker listo:", registration);

        // Enviar un mensaje "ping" para despertar el Service Worker
        if (navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({
            type: "PING",
            timestamp: Date.now(),
          });
        }
      });
    }
  }, []);

  // Derivar si se debe mostrar la notificación de cumpleaños (sin useEffect)
  const deberiasMostrarNotificacionCumple =
    mostrarNotificacionOriginal && !cumpleDismissed;

  // Crear wrapper para cerrar notificación
  const cerrarNotificacionCumple = () => {
    setCumpleDismissed(true);
    cerrarNotificacionCumpleOriginal();
  };

  // Verificar autenticación cuando cambia la ubicación
  useEffect(() => {
    const syncAuthentication = () => setIsAuthenticated(checkAuth());
    syncAuthentication();
    window.addEventListener(AUTH_STATE_CHANGED_EVENT, syncAuthentication);

    return () =>
      window.removeEventListener(AUTH_STATE_CHANGED_EVENT, syncAuthentication);
  }, []);

  useEffect(() => {
    const isLoggedIn = checkAuth();

    // Solo verificar pendientes al cambiar de ruta (no cumpleaños)
    if (isLoggedIn && location.pathname !== "/") {
      verificarPendientes();
    }
  }, [location.pathname]);

  // Socket.io — conectar cuando está autenticado, escuchar force-logout
  useEffect(() => {
    if (!isAuthenticated) {
      disconnectSocket();
      return;
    }

    const socket = connectSocket();
    if (!socket) return;

    const handleForceLogout = ({ reason, titulo, mensaje } = {}) => {
      console.warn("🔌 Force-logout recibido:", reason);
      disconnectSocket();
      const customData = titulo || mensaje ? { titulo, mensaje } : null;
      emitSessionExpired(
        "Tu sesión fue cerrada desde otro dispositivo.",
        customData,
      );
    };

    // 🔌 Cambio de rol en tiempo real: actualizar storage, renovar token y redirigir
    const handleRoleChanged = async ({ idrol, idplanta }) => {
      console.log("🔌 role-changed recibido:", { idrol, idplanta });

      // Mapeo de rol a ruta home
      const roleHomeMap = {
        1: "/homeadmin",
        2: "/homeinspector",
        3: "/homesupervisor",
        4: "/homepage",
        5: "/homedeveloper",
        6: "/homegeneral",
        7: "/homerh",
        8: "/homeinstalacion",
      };

      const roleNameMap = {
        1: "Administrador",
        2: "Inspector",
        3: "Supervisor",
        4: "Default",
        5: "Developer",
        6: "General",
        7: "Recursos Humanos",
        8: "Instalación",
      };

      // Actualizar storage con el nuevo rol/planta
      const storage =
        localStorage.getItem("mantenerSesion") === "true"
          ? localStorage
          : sessionStorage;
      storage.setItem("userRole", idrol);
      storage.setItem("userPlanta", idplanta || "");

      // Renovar access token para que el JWT refleje el nuevo rol
      try {
        await apiClient.post("/refresh-access-token");
      } catch (err) {
        console.warn("Error renovando token tras cambio de rol:", err);
      }

      // Mostrar notificación y redirigir
      const destino = roleHomeMap[idrol] || "/homepage";
      const nombreRol = roleNameMap[idrol] || "Desconocido";
      sileo.info({
        title: "Cambio de rol",
        description: (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span>
              Se te ha asignado el rol de <strong>{nombreRol}</strong>
            </span>
            <span style={{ fontSize: "0.85em", opacity: 0.75 }}>
              Serás redirigido en unos segundos...
            </span>
          </div>
        ),
        duration: 5000,
      });

      // Hard reload para reinicializar toda la app con el nuevo rol
      setTimeout(() => {
        window.location.href = destino;
      }, 3000);
    };

    socket.on("force-logout", handleForceLogout);
    socket.on("role-changed", handleRoleChanged);

    return () => {
      socket.off("force-logout", handleForceLogout);
      socket.off("role-changed", handleRoleChanged);
    };
  }, [isAuthenticated]);

  // Verificar cumpleaños UNA VEZ al cargar la app (el servicio controla con localStorage que sea 1 vez al día)
  useEffect(() => {
    const checkAuthentication = async () => {
      if (checkAuth()) {
        try {
          await verificarPendientes();
          await verificarCumpleanos();
          setCumpleDismissed(false);
        } catch (error) {
          console.error("Error verificando notificaciones:", error);
        }
      }
    };

    checkAuthentication();
  }, []);

  // Efecto para forzar redibujado cuando cambia el tema
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "darkTheme") {
        // Forzar un redibujado
        document.body.style.display = "none";
        setTimeout(() => {
          document.body.style.display = "";
        }, 50);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  return (
    <ErrorBoundary>
      <DemoProvider>
        <LayoutProvider>
          <ThemeWrapper>
            <GlobalSkeletonProvider>
              <ThemedToaster />
              <Layout>
                <Suspense fallback={<div></div>}>
                  {/* ✅ Todas las rutas ahora vienen de /app/routes/index.jsx */}
                  <Routes>{allRoutes}</Routes>
                </Suspense>

                {/* Componentes globales de la aplicación */}
                {isAuthenticated && <InAppNotifications />}
                {isAuthenticated && <InstallPWA />}

                {mostrarNotificacion && (
                  <NotificacionPendientes
                    cantidad={pendientesSinCompletar}
                    onClose={cerrarNotificacion}
                  />
                )}

                {deberiasMostrarNotificacionCumple && (
                  <CumpleanosNotificacion
                    esCumpleanos={esCumpleanos}
                    cumpleaneros={cumpleaneros}
                    nombreUsuario={nombreUsuario}
                    onClose={cerrarNotificacionCumple}
                  />
                )}
                <UpdateNotification />

                {/* Controles de demo */}
                <DemoControls />
              </Layout>

              {/* Banner de consentimiento de cookies (fuera del Layout para z-index global) */}
              <CookieConsent />

              {/* Modal de sesión expirada (z-index máximo, bloquea toda la UI) */}
              <SessionExpiredModal />
            </GlobalSkeletonProvider>
          </ThemeWrapper>
        </LayoutProvider>
      </DemoProvider>
    </ErrorBoundary>
  );
}

export default App;
